import React, { useState } from 'react';
import { Clock, Tag, ArrowRight, BookOpen } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { blogPosts } from '../data/blogPosts';

const categories = ['Tous', ...new Set(blogPosts.map(p => p.category))];

const BlogPage = () => {
  const [activeCategory, setActiveCategory] = useState('Tous');

  const filtered = activeCategory === 'Tous'
    ? blogPosts
    : blogPosts.filter(p => p.category === activeCategory);

  return (
    <div className="min-h-screen bg-cream">
      <Header />

      {/* Hero */}
      <section
        className="pt-36 pb-20 text-center"
        style={{ background: 'linear-gradient(to bottom right, hsl(0,0%,12%), hsl(0,64%,20%), hsl(0,0%,12%))' }}
      >
        <div className="max-w-3xl mx-auto px-4">
          <div className="inline-flex items-center justify-center w-14 h-14 bg-gold/20 rounded-full border-2 border-gold/40 mb-6">
            <BookOpen className="w-7 h-7 text-gold" />
          </div>
          <p className="text-gold font-medium mb-2 uppercase tracking-widest text-sm">Conseils & Actualités</p>
          <h1 className="text-4xl sm:text-5xl font-bold text-cream mb-4">Le Blog</h1>
          <div className="w-16 h-1 bg-gold mx-auto mb-6" />
          <p className="text-cream/70 text-lg max-w-xl mx-auto">
            Conseils hygiène, traditions culinaires, astuces traiteur… Tout ce qu'il faut savoir pour organiser vos événements.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="bg-white border-b border-gold/20 py-5 sticky top-0 z-40 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex gap-2 flex-wrap justify-center">
          {categories.map(cat => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all ${
                activeCategory === cat
                  ? 'bg-gold text-charcoal shadow'
                  : 'bg-cream text-charcoal/60 hover:bg-gold/10 hover:text-charcoal'
              }`}
            >
              {cat}
            </button>
          ))}
        </div>
      </section>

      {/* Articles grid */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((post) => (
              <article
                key={post.slug}
                className="bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 group flex flex-col border border-gold/10"
              >
                <div className="relative overflow-hidden h-52">
                  <img
                    src={post.image}
                    alt={post.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={e => { e.target.style.background = '#f5e6d0'; e.target.style.display = 'none'; }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-charcoal/40 to-transparent" />
                  <span className="absolute top-4 left-4 bg-gold text-charcoal text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Tag className="w-3 h-3" />
                    {post.category}
                  </span>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-4 text-xs text-charcoal/40 mb-3">
                    <span>{post.date}</span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {post.readTime}
                    </span>
                  </div>

                  <h2 className="text-lg font-bold text-charcoal mb-3 leading-snug group-hover:text-burgundy transition-colors flex-1">
                    {post.title}
                  </h2>

                  <p className="text-charcoal/60 text-sm leading-relaxed mb-5 line-clamp-3">
                    {post.excerpt}
                  </p>

                  <Link
                    to={`/blog/${post.slug}`}
                    className="inline-flex items-center gap-2 text-gold font-semibold text-sm hover:gap-3 transition-all"
                  >
                    Lire l'article
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {filtered.length === 0 && (
            <div className="text-center py-20 text-charcoal/40">
              <p className="text-xl">Aucun article dans cette catégorie.</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-charcoal text-center">
        <div className="max-w-2xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-cream mb-4">Un événement à organiser ?</h2>
          <p className="text-cream/60 mb-8">Contactez-nous pour un devis gratuit et personnalisé.</p>
          <Link
            to="/#booking"
            onClick={() => {
              setTimeout(() => {
                const el = document.getElementById('booking');
                if (el) el.scrollIntoView({ behavior: 'smooth' });
              }, 100);
            }}
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-charcoal font-bold rounded-lg hover:bg-gold/90 transition-all shadow-xl"
          >
            Demander un devis
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BlogPage;
